import { useState } from 'react';
import { academyMediaUrl } from '@/lib/api';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Info, Lightbulb, AlertTriangle, Mail, Phone, Clock } from 'lucide-react';

export function RichText({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => (p.startsWith('**') ? <strong key={i} className="font-semibold text-navy dark:text-white">{p.slice(2, -2)}</strong> : p));
}

const BOX = {
  info: { icon: Info, cls: 'bg-blue-50 border-blue-500 dark:bg-blue-900/20', label: 'Information' },
  tip: { icon: Lightbulb, cls: 'bg-emerald-50 border-emerald-500 dark:bg-emerald-900/20', label: 'Tips' },
  warning: { icon: AlertTriangle, cls: 'bg-orange-50 border-orange-500 dark:bg-orange-900/20', label: 'Obs' },
};

function Callout({ kind, title, text }) {
  const { icon: Icon, cls, label } = BOX[kind];
  return (
    <div className={`border-l-4 rounded-r-lg p-4 ${cls}`} data-testid={`academy-callout-${kind}`}>
      <p className="flex items-center gap-2 text-xs font-ibm font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200">
        <Icon className="h-4 w-4" />{title || label}
      </p>
      <p className="text-sm text-slate-700 dark:text-slate-300 mt-1.5 font-ibm leading-relaxed"><RichText text={text} /></p>
    </div>
  );
}

function Screenshot({ src, caption, width }) {
  const [open, setOpen] = useState(false);
  return (
    <figure className={width ? 'max-w-xs' : ''}>
      <button onClick={() => setOpen(true)} className="block w-full rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow" data-testid={`academy-image-${src}`}>
        <img src={academyMediaUrl(src)} alt={caption} loading="lazy" className="w-full h-auto" />
      </button>
      {caption && <figcaption className="text-xs text-slate-400 font-ibm italic mt-2">{caption}</figcaption>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl p-2">
          <DialogTitle className="sr-only">{caption}</DialogTitle>
          <img src={academyMediaUrl(src)} alt={caption} className="w-full h-auto rounded" />
        </DialogContent>
      </Dialog>
    </figure>
  );
}

function SupportCard({ support }) {
  const rows = [[Mail, support.email, `mailto:${support.email}`], [Phone, support.phone, `tel:${support.phone.replace(/[^0-9+]/g, '')}`], [Clock, support.hours]];
  return (
    <div className="rounded-lg bg-navy text-white p-5 grid gap-3 sm:grid-cols-3" data-testid="academy-support-card">
      {rows.map(([Icon, label, href]) => (
        <div key={label} className="flex items-center gap-3 font-ibm text-sm">
          <Icon className="h-4 w-4 text-red-400 shrink-0" />
          {href ? <a href={href} className="hover:underline">{label}</a> : <span>{label}</span>}
        </div>
      ))}
    </div>
  );
}

function ListBlock({ items, ordered, check }) {
  return (
    <ul className="space-y-2.5">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3 text-sm font-ibm text-slate-700 dark:text-slate-300 leading-relaxed">
          {ordered ? (
            <span className="h-6 w-6 rounded-full bg-navy text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
          ) : check ? (
            <span className="h-4 w-4 mt-0.5 rounded border-2 border-slate-300 shrink-0" />
          ) : (
            <span className="h-1.5 w-1.5 mt-2 rounded-full bg-red-500 shrink-0" />
          )}
          <span><RichText text={it} /></span>
        </li>
      ))}
    </ul>
  );
}

function TableBlock({ header, rows }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
      <table className="w-full text-sm font-ibm">
        <thead className="bg-navy text-white">
          <tr>{header.map(h => <th key={h} className="text-left px-3 py-2 font-semibold">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100 dark:border-slate-700 even:bg-slate-50 dark:even:bg-slate-800/50">
              {r.map((c, j) => <td key={j} className={`px-3 py-2 align-top ${j === 0 ? 'font-semibold text-navy dark:text-white' : 'text-slate-600 dark:text-slate-300'}`}><RichText text={c} /></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Faq({ items }) {
  return (
    <div className="space-y-4">
      {items.map(it => (
        <div key={it.q}>
          <p className="font-ibm font-semibold text-navy dark:text-white text-sm">{it.q}</p>
          <p className="font-ibm text-sm text-slate-600 dark:text-slate-300 mt-1 leading-relaxed">{it.a}</p>
        </div>
      ))}
    </div>
  );
}

export function AcademyBlocks({ blocks, support }) {
  return (
    <div className="space-y-4">
      {blocks.map((b, i) => {
        switch (b.type) {
          case 'p': return <p key={i} className="text-sm md:text-base font-ibm text-slate-700 dark:text-slate-300 leading-relaxed"><RichText text={b.text} /></p>;
          case 'steps': return <ListBlock key={i} items={b.items} ordered />;
          case 'bullets': return <ListBlock key={i} items={b.items} />;
          case 'checklist': return <ListBlock key={i} items={b.items} check />;
          case 'image': return <Screenshot key={i} src={b.src} caption={b.caption} width={b.width} />;
          case 'table': return <TableBlock key={i} header={b.header} rows={b.rows} />;
          case 'faq': return <Faq key={i} items={b.items} />;
          case 'support': return <SupportCard key={i} support={support} />;
          default: return BOX[b.type] ? <Callout key={i} kind={b.type} title={b.title} text={b.text} /> : null;
        }
      })}
    </div>
  );
}
